import { PencilIcon } from "@primer/octicons-react";
import { Box, Label, SelectPanel } from "@primer/react";
import { useMemo, useState } from "react";
import { sf_hinhthucmiengiam_vm } from "../../models/response/category/sf_hinhthucmiengiam";
import { RootState } from "../../state/reducers";
import { useSelector } from "react-redux";

type SelectBoxMultipleHinhThucMienGiamProps = {
  isReadonly?: boolean;
  value?: number[];
  onValueChanged: (ids: number[]) => void;
  className?: string;
  preText?: string;
  width?: string | number;
  stylingMode?: "outlined" | "filled" | "underlined";
  isShowClearBtn?: boolean;
  maxWidth?: any;
  sf_hinhthucmiengiams: sf_hinhthucmiengiam_vm[];
};

const SelectBoxMultipleHinhThucMienGiam = (
  props: SelectBoxMultipleHinhThucMienGiamProps
) => {
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(false);
  const { dm_coso_id } = useSelector((state: RootState) => state.common);

  const removeVietnameseTones = (str: string) => {
    return str
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/đ/g, "d")
      .replace(/Đ/g, "D");
  };

  // Tạo danh sách items với divider giữa các groups (sắp xếp theo thutu_nhommiengiam)
  const allSelectableItems = useMemo(() => {
    const filteredData = [...props.sf_hinhthucmiengiams]
      .filter((x) => !dm_coso_id || x.dm_coso_id === dm_coso_id)
      .sort(
        (a, b) =>
          (a.thutu_nhommiengiam ?? 999) - (b.thutu_nhommiengiam ?? 999) ||
          (a.sf_hinhthucmiengiam_group_id ?? 0) -
            (b.sf_hinhthucmiengiam_group_id ?? 0) ||
          a.id - b.id
      );

    const groups: Record<
      string,
      {
        id: number;
        text: string;
        shortCode: string;
        fullName: string;
        groupName: string;
        groupId: number;
      }[]
    > = {};

    filteredData.forEach((item) => {
      const group = item.ten_nhom_mien_giam || "Khác";
      if (!groups[group]) {
        groups[group] = [];
      }
      const displayLabel =
        item.hinh_thuc_mien_giam &&
        item.hinh_thuc_mien_giam !== item.ma_hinh_thuc
          ? `${item.ma_hinh_thuc} - ${item.hinh_thuc_mien_giam}`
          : item.ma_hinh_thuc;
      groups[group].push({
        id: item.id,
        text: displayLabel,
        shortCode: item.ma_hinh_thuc,
        fullName: item.hinh_thuc_mien_giam || item.ma_hinh_thuc,
        groupName: group,
        groupId: item.sf_hinhthucmiengiam_group_id || 0,
      });
    });

    const result: any[] = [];
    const groupEntries = Object.entries(groups);

    groupEntries.forEach(([groupName, items]) => {
      result.push({
        id: `divider-${groupName}`,
        text: `Nhóm: ${groupName}`,
        isDivider: true,
        disabled: true,
      });
      result.push(...items);
    });

    return result;
  }, [props.sf_hinhthucmiengiams, dm_coso_id]);

  const filteredItems = useMemo(() => {
    if (!filter) return allSelectableItems;

    const normalizedFilter = removeVietnameseTones(filter.toLowerCase());
    return allSelectableItems.filter(
      (item) =>
        !item.isDivider &&
        (removeVietnameseTones(item.text.toLowerCase()).includes(
          normalizedFilter
        ) ||
          removeVietnameseTones(item.groupName?.toLowerCase() || "").includes(
            normalizedFilter
          ))
    );
  }, [allSelectableItems, filter]);

  const _selectedData = useMemo(() => {
    return allSelectableItems.filter(
      (item) => !item.isDivider && props.value?.includes(item.id)
    );
  }, [props.value, allSelectableItems]);

  return (
    <SelectPanel
      renderAnchor={({ "aria-labelledby": ariaLabelledBy, ...anchorProps }) => (
        <Box
          as="button"
          type="button"
          aria-labelledby={ariaLabelledBy}
          {...anchorProps}
          sx={{
            width: "100%",
            maxWidth: props.maxWidth || "100%",
            display: "flex",
            flexWrap: "wrap",
            gap: "4px",
            alignItems: "center",
            justifyContent: "flex-start",
            textAlign: "left",
            whiteSpace: "normal",
            backgroundColor: "transparent",
            border: "1px dashed transparent",
            borderRadius: "6px",
            cursor: "pointer",
            padding: "4px 6px",
            boxSizing: "border-box",
            "&:hover": {
              backgroundColor: "canvas.subtle",
              borderColor: "border.default",
            },
          }}
        >
          {_selectedData.length === 0 ? (
            <Box
              sx={{
                fontSize: 12,
                color: "fg.subtle",
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                border: "1px solid",
                borderColor: "border.subtle",
                borderRadius: "20px",
                px: 2,
                py: "2px",
                backgroundColor: "canvas.default",
              }}
            >
              <span>Chọn miễn giảm</span>
              <PencilIcon size={13} />
            </Box>
          ) : (
            <>
              {_selectedData.map((item) => (
                <Label
                  key={item.id}
                  size="small"
                  title={item.text}
                  style={{ fontSize: 12 }}
                  sx={{
                    backgroundColor: "accent.subtle",
                    color: "accent.fg",
                    borderColor: "accent.muted",
                    fontWeight: 600,
                    paddingX: "8px",
                    paddingY: "2px",
                    borderRadius: "20px",
                    fontSize: 1,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    maxWidth: "100%",
                  }}
                >
                  <Box as="span" sx={{ color: "accent.fg" }}>
                    •
                  </Box>
                  <Box
                    as="span"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {item.shortCode || item.text}
                  </Box>
                </Label>
              ))}
              <Box
                as="span"
                sx={{
                  color: "fg.muted",
                  display: "inline-flex",
                  alignItems: "center",
                  ml: "2px",
                }}
              >
                <PencilIcon size={14} />
              </Box>
            </>
          )}
        </Box>
      )}
      title="Chọn hình thức miễn giảm (tối đa 1 mục/nhóm)"
      placeholderText="Tìm theo mã, tên hoặc nhóm miễn giảm..."
      open={open}
      onOpenChange={setOpen}
      onFilterChange={setFilter}
      showItemDividers={true}
      overlayProps={{ width: "large", height: "medium" }}
      items={filter ? filteredItems : allSelectableItems}
      selected={_selectedData}
      onSelectedChange={(selectedItems: any[]) => {
        if (selectedItems.length === 0) {
          props.onValueChanged([]);
          return;
        }

        // Giữ tối đa 1 mục cho mỗi groupId > 0 (ưu tiên mục chọn sau cùng)
        const seenGroups = new Set<number>();
        const uniqueItems: any[] = [];

        for (let i = selectedItems.length - 1; i >= 0; i--) {
          const item = selectedItems[i];
          const selectedItem = allSelectableItems.find(
            (x) => x.id === item.id && !x.isDivider
          );
          if (!selectedItem) continue;

          if (selectedItem.groupId > 0) {
            if (seenGroups.has(selectedItem.groupId)) continue;
            seenGroups.add(selectedItem.groupId);
          }
          uniqueItems.unshift(selectedItem);
        }

        const newValues = uniqueItems.map((item) => item.id);
        props.onValueChanged(newValues);
      }}
    />
  );
};

export default SelectBoxMultipleHinhThucMienGiam;
